import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router'

import { TRPCProvider } from '@langmail/shared-ui'

import { AdminRoute } from '@/components/admin-route'
import { AppLayout } from '@/components/app-layout'
import { ProtectedRoute } from '@/components/protected-route.tsx'
import { Toaster } from '@/components/ui/toaster'
import { TooltipProvider } from '@/components/ui/tooltip'
import { AuthProvider } from '@/features/auth/contexts/auth-context'
import { trpcClient } from '@/lib/trpc'

import { Access } from './features/access/pages/access'
import { Accounts } from './features/account/pages/accounts'
import { Activity } from './features/activity/pages/activity'
import { Auth } from './features/auth/pages/auth'
import { AuthCallback } from './features/auth/pages/auth-callback'
import { ForgotPassword } from './features/auth/pages/forgot-password'
import { Reconnect } from './features/auth/pages/reconnect'
import { ResetPassword } from './features/auth/pages/reset-password'
import { Success } from './features/auth/pages/success'
import { Subscribe } from './features/billing/pages/subscribe'
import { MorningBrief } from './features/morning-brief/pages/morning-brief'
import { AdminPage } from './pages/admin'
import { NotFound } from './pages/not-found'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

export const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TRPCProvider queryClient={queryClient} trpcClient={trpcClient}>
        <TooltipProvider>
          <AuthProvider>
            <Toaster />
            <BrowserRouter>
              <Routes>
                {/* Public routes */}
                <Route element={<Auth />} path="/login" />
                <Route element={<ForgotPassword />} path="/forgot-password" />
                <Route element={<ResetPassword />} path="/reset-password" />
                <Route element={<Reconnect />} path="/reconnect" />
                <Route element={<AuthCallback />} path="/auth/callback/google" />
                <Route element={<Success />} path="/success" />
                {/* A sibling of the gated layout block, not a child: the layout's
                    default ProtectedRoute would bounce an ended trial here first. */}
                <Route
                  element={
                    <ProtectedRoute requireSubscription={false}>
                      <Subscribe />
                    </ProtectedRoute>
                  }
                  path="/subscribe"
                />
                <Route element={<Navigate replace to="/activity" />} path="/" />

                {/* Protected routes with shared AppLayout */}
                <Route
                  element={
                    <ProtectedRoute>
                      <AppLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route element={<Activity />} path="/activity" />
                  <Route element={<Access />} path="/access" />
                  <Route element={<MorningBrief />} path="/morning-brief" />
                  {/* Not /account: nginx 301s that retired prefix to the root (ADR 0017). */}
                  <Route element={<Accounts />} path="/settings" />
                  <Route
                    element={
                      <AdminRoute>
                        <AdminPage />
                      </AdminRoute>
                    }
                    path="/admin"
                  />
                </Route>

                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route element={<NotFound />} path="*" />
              </Routes>
            </BrowserRouter>
          </AuthProvider>
        </TooltipProvider>
      </TRPCProvider>
    </QueryClientProvider>
  )
}
